import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/shared/utils/cn";

const buttonVariants = cva(
  "inline-flex items-center select-none justify-center whitespace-nowrap rounded-sm transition-colors duration-300 focus-visible:outline-none  disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        default:
          "bg-black text-white hover:bg-black/90 rounded-md py-4 font-md",
        destructive:
          "bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90 font-md",
        outline:
          "border border-foreground bg-background hover:bg-accent hover:text-accent-foreground hover:border-accent font-rg",
        secondary:
          "bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80 font-rg",
        ghost:
          "text-black hover:text-black font-rg transition-colors duration-300",
        link: "text-primary underline-offset-4 hover:underline font-rg",
        gallery:
          "text-dark bg-neutral-100 hover:text-black hover:bg-neutral-200 rounded-md select-none font-md",
        date: "rounded-sm border border-lighter hover:border-light bg-lighter hover:bg-light focus:border-blue font-rg",
        social: "bg-white border border-light hover:bg-lighter",
        dots: "bg-lighter hover:bg-light",
        plat: "bg-light text-black hover:bg-lighter rounded-md font-md",
      },
      size: {
        default: "px-4 h-[36px] text-sm font-sb",
        rounded: "rounded-full px-5 h-[32px] text-base",
        sm: "h-8 rounded-md px-3 text-xs",
        lg: "h-10 rounded-md px-8",
        icon: "h-9 w-9",
        nav: "gap-[5px] h-[24px] px-[14px] text-black text-sm",
        nav2: "gap-[5px] h-[24px] w-[24px] text-black text-sm",
        social: "h-8 w-8 rounded-lg flex items-center justify-center",
        dots: "h-6 w-6 rounded-md flex items-center justify-center text-dark hover:text-black",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
